import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { wrapThunkAction } from 'redux/utils';
import NvTable, { Column, RowProps } from 'shared/components/nv-table';
import { AngularContext } from 'react-app';
import MentoringProgramContext, { MentoringProgramHomeUIContext } from 'athena/components/mentoring-program/context';
import { standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import {
  getMentorshipProgramActionItemTemplates,
  createMentorshipProgramActionItemTemplate,
  updateMentorshipProgramActionItemTemplate,
  deleteMentorshipProgramActionItemTemplate,
} from 'redux/actions/mentoring-program-action-item-templates';
import { getProgramActionItemTemplates } from 'redux/selectors/mentoring-program-action-item-templates';
import { MentoringProgramActionItemTemplate } from 'redux/schemas/models/mentoring-program-action-item-templates';
import ActionItemRow from './action-item-row';
import {
  ADD_LINK_ID,
  ColumnID,
  NEW_ACTION_ITEM_ID,
  PreAssignedActionItem,
  PreAssignedActionItemFormData,
} from './types';

type PreAssignedActionItemsProps = {
  onScroll: React.ComponentProps<'div'>['onScroll'];
  scrollEnabled: boolean;
  onHasUnsavedChanges?: (hasChanges: boolean) => void;
};

const templateToRow = (template: MentoringProgramActionItemTemplate): PreAssignedActionItem => ({
  id: template.id,
  description: template.description,
  assignedRole: template.assignedRole,
  required: template.required,
});

const PreAssignedActionItems = ({ onScroll, scrollEnabled, onHasUnsavedChanges }: PreAssignedActionItemsProps) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { withOverflowFlexGrowClass } = useContext(MentoringProgramHomeUIContext);
  const { $scope, injectServices } = useContext(AngularContext);
  const [ConfirmationOverlays, $location] = injectServices(['ConfirmationOverlays', '$location']);
  const dispatch = useAppDispatch();
  const styles = css`
    .table-body {
      overflow-y: ${scrollEnabled ? 'auto' : 'hidden'};
    }
  `;
  const templates = useSelector(getProgramActionItemTemplates(mentoringProgram?.id ?? 0));

  const hasUnsavedChangesRef = useRef(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddRow, setShowAddRow] = useState(false);

  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    hasUnsavedChangesRef.current = hasChanges;
    onHasUnsavedChanges?.(hasChanges);
  }, [onHasUnsavedChanges]);

  useEffect(() => {
    if (mentoringProgram?.id) {
      dispatch(getMentorshipProgramActionItemTemplates({ mentoringProgramId: mentoringProgram.id }));
    }
  }, [dispatch, mentoringProgram?.id]);

  useEffect(() => {
    const deregister = $scope.StateManager.registerStateChangeStart(
      () => hasUnsavedChangesRef.current,
      'athena/templates/action-items-navigate-away.html',
      'MENTORING_PROGRAMS.TEMPLATES.SETTINGS.NAVIGATE_AWAY.UNSAVED_CHANGES',
    );
    return deregister;
  }, [$scope.StateManager]);

  useEffect(() => {
    const deregister = $scope.$root.$on('$locationChangeStart', (event: any, newUrl: string) => {
      if (!hasUnsavedChangesRef.current) return;

      // $locationChangeStart also fires for $state.go() navigations. Those go through
      // the patched $state.transitionTo, which prevents $stateChangeStart and opens the
      // StateManager modal. Calling event.preventDefault() here stops $stateChangeStart
      // from firing, so the StateManager never sees those navigations — both paths end
      // up here instead, which is fine: we open the same modal template and re-navigate
      // via $location.url() on confirmation.
      event.preventDefault();
      // AngularJS reverts the URL via history.replaceState(), which does not fire
      // a popstate event. React Router (BrowserRouter) uses popstate to track location,
      // so without this it stays stuck on the clicked URL and the wrong tab appears
      // active after Cancel. Dispatching a synthetic popstate on the next tick — after
      // AngularJS has finished reverting — forces React Router to re-sync.
      setTimeout(() => {
        const popstate = typeof PopStateEvent === 'function'
          ? new PopStateEvent('popstate', { state: window.history.state })
          : new Event('popstate');
        window.dispatchEvent(popstate);
      }, 0);

      ConfirmationOverlays.openConfirmationModal(
        'athena/templates/action-items-navigate-away.html',
      ).result.then(() => {
        setHasUnsavedChanges(false);
        // newUrl is the full URL (e.g. http://host/#!/mentoring-programs/38/communications).
        // Extract the hash path (strip the leading "!" from the hashbang prefix).
        const hashPath = newUrl.split('#')[1]?.replace(/^!/, '') || '/';
        $location.url(hashPath);
      });
    });

    return deregister;
  }, [$scope.$root, $location, ConfirmationOverlays, setHasUnsavedChanges]);

  const tableData = useMemo<PreAssignedActionItem[]>(() => {
    const addLink: PreAssignedActionItem = { id: ADD_LINK_ID, description: '', assignedRole: null, required: false };
    const newRow: PreAssignedActionItem = { id: NEW_ACTION_ITEM_ID, description: '', assignedRole: null, required: false };
    const rows = templates.map(templateToRow);
    return showAddRow ? [newRow, ...rows] : [addLink, ...rows];
  }, [templates, showAddRow]);

  const handleSave = useCallback((
    formData: PreAssignedActionItemFormData,
    itemId: number | typeof NEW_ACTION_ITEM_ID,
  ) => {
    if (!mentoringProgram?.id) return;
    if (!formData.assignedRole) return;
    const payload = {
      mentoringProgramId: mentoringProgram.id,
      mentorshipProgramActionItemTemplate: { ...formData, assignedRole: formData.assignedRole },
    };
    if (itemId === NEW_ACTION_ITEM_ID) {
      wrapThunkAction(dispatch(createMentorshipProgramActionItemTemplate(payload))).then(() => {
        setHasUnsavedChanges(false);
        setShowAddRow(false);
      });
    } else {
      wrapThunkAction(dispatch(updateMentorshipProgramActionItemTemplate({ ...payload, templateId: itemId }))).then(() => {
        setHasUnsavedChanges(false);
        setEditingId(null);
      });
    }
  }, [dispatch, mentoringProgram?.id, setHasUnsavedChanges]);

  const handleDelete = useCallback((id: number) => {
    const translationBase = t.MENTORING_PROGRAMS.TEMPLATES.SETTINGS.DELETE_ACTION_ITEM;
    dispatch(openConfirmationDialog({
      title: translationBase.TITLE(),
      bodyText: translationBase.BODY(),
      confirmText: t.FORM.DELETE(),
      onConfirm: () => {
        if (!mentoringProgram?.id) return;
        dispatch(deleteMentorshipProgramActionItemTemplate({
          mentoringProgramId: mentoringProgram.id,
          templateId: id,
        }));
      },
    }));
  }, [dispatch, mentoringProgram?.id]);

  const confirmDiscardIfNeeded = useCallback((proceed: () => void) => {
    if (!hasUnsavedChangesRef.current) {
      proceed();
      return;
    }
    ConfirmationOverlays.openConfirmationModal(
      'athena/templates/action-items-navigate-away.html',
    ).result.then(() => {
      setHasUnsavedChanges(false);
      proceed();
    });
  }, [ConfirmationOverlays, setHasUnsavedChanges]);

  const handleSetShowAddRow = useCallback((show: boolean) => {
    confirmDiscardIfNeeded(() => {
      setShowAddRow(show);
      if (show) setEditingId(null);
    });
  }, [confirmDiscardIfNeeded]);

  const handleSetEditingId = useCallback((id: number | null) => {
    confirmDiscardIfNeeded(() => {
      setEditingId(id);
      if (id) setShowAddRow(false);
    });
  }, [confirmDiscardIfNeeded]);

  const RowComponent = useCallback(
    (rowProps: RowProps<PreAssignedActionItem>) => (
      <ActionItemRow
        {...rowProps}
        editingId={editingId}
        setEditingId={handleSetEditingId}
        setShowAddRow={handleSetShowAddRow}
        onSave={handleSave}
        onDelete={handleDelete}
        onHasUnsavedChanges={setHasUnsavedChanges}
      />
    ),
    [editingId, handleSetEditingId, handleSetShowAddRow, handleSave, handleDelete, setHasUnsavedChanges],
  );

  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.ACTION_ITEMS;
  const columns: Column<PreAssignedActionItem>[] = useMemo(() => [
    {
      id: ColumnID.DESCRIPTION,
      header: translationBase.COLUMNS.ACTION_ITEM(),
      accessorFn: (item) => item.description,
      gridTemplateColumn: '6fr',
    },
    {
      id: ColumnID.ASSIGNEE,
      header: translationBase.COLUMNS.ASSIGNEE(),
      accessorFn: (item) => item.assignedRole,
      gridTemplateColumn: '4fr',
    },
    {
      id: ColumnID.REQUIRED,
      header: translationBase.COLUMNS.REQUIRED(),
      accessorFn: (item) => item.required,
      gridTemplateColumn: '1fr',
    },
    {
      id: ColumnID.OPTIONS,
      accessorFn: (item) => item,
      gridTemplateColumn: '0.5fr',
    },
  ], [translationBase]);

  return (
    <NvTable<PreAssignedActionItem>
      css={styles}
      data={tableData}
      columns={columns}
      className={`d-flex flex-column ${withOverflowFlexGrowClass}`}
      RowComponent={RowComponent}
      bodyProps={{ onScroll, className: `table-body ${withOverflowFlexGrowClass}` }}
      spacing={threeQuartersSpacing}
      sidesSpacing={standardSpacing}
    />
  );
};

export default PreAssignedActionItems;
